import { useCallback, useEffect, useRef } from 'react';
import { toast } from 'react-hot-toast';

import { PERMISSIONS_CHANGED_EVENT } from '../utils/permissions';
import { activeT, errorText } from '../utils/apiErrors';

const apiUrl = process.env.REACT_APP_API_URL || '';

// Backend refusals that mean the caller's own access changed, rather than the
// caller simply asking for something they were never allowed. Every endpoint is
// authorized server-side now, so a role change lands as one of these on the
// next request — the cached profile in localStorage is stale at that point and
// the UI is still offering actions that will keep failing.
const STALE_PERMISSION_CODES = new Set([
    'access_denied',
    'access_disabled',
    'insufficient_permissions',
    'invalid_role',
]);

export const useApi = (googleIdToken, handleLogout) => {
  // Almost every caller passes an inline arrow (`() => window.location.reload()`),
  // which is a new function on every render. If that landed in apiRequest's
  // dependency list, apiRequest would get a new identity on every render — and
  // any component that put it in a useCallback/useEffect dependency array would
  // re-run its fetch, set state, re-render, and fetch again, forever. So keep
  // the callback in a ref and depend only on the token.
  //
  // The alternative — telling each caller to memoize its handler — is a rule
  // that has to be remembered at nine call sites and will be missed at the
  // tenth. It is only read on a 401, so a ref costs nothing.
  const handleLogoutRef = useRef(handleLogout);
  useEffect(() => {
    handleLogoutRef.current = handleLogout;
  }, [handleLogout]);

  const apiRequest = useCallback(async (path, options = {}) => {
    const url = `${apiUrl}${path}`;

    const headers = {
      'Authorization': `Bearer ${googleIdToken}`,
      ...options.headers,
    };

    // Automatically add Content-Type for requests with a body
    if (options.body && !headers['Content-Type']) {
      headers['Content-Type'] = 'application/json';
    }

    const config = {
      ...options,
      headers,
    };

    try {
      const res = await fetch(url, config);

      if (res.status === 401) {
        // Optional: CustomerSummary calls useApi with no logout handler at all,
        // where this used to throw a TypeError over the real 401.
        handleLogoutRef.current?.();
        const err = new Error('Unauthorized');
        err.status = 401;
        throw err;
      }

      // Try to parse JSON regardless of status, as error responses might have a body.
      const responseData = await res.json().catch(() => ({})); // Gracefully handle non-JSON responses

      // A 403 whose code says the caller's own access changed: refresh the
      // cached profile so the UI stops showing controls the backend refuses,
      // and say so once rather than letting every action fail silently.
      // Codes like customer_out_of_scope are NOT in that set — those are a
      // normal refusal for the component to handle, not a stale profile.
      if (res.status === 403 && STALE_PERMISSION_CODES.has(responseData.error)) {
        // A hook, so there is no `t` prop here; activeT() is the language
        // App.js is currently rendering in. Falls back to the server's English
        // sentence when a code has no translation yet.
        toast.error(errorText(responseData, activeT(), 'Your access has changed.'), {
          id: 'permissions-changed',
        });
        window.dispatchEvent(
          new CustomEvent(PERMISSIONS_CHANGED_EVENT, { detail: responseData })
        );
      }

      if (!res.ok) {
        // `message` first: the route guard puts a machine code in `error` and
        // the sentence in `message`, so using `error` here surfaced raw codes
        // like "insufficient_permissions" in any toast built from
        // error.message. Older handlers send only `error`, holding human text,
        // which this ordering still picks up. errorText() in utils/apiErrors
        // does the same job with translations when a component has `t`.
        const err = new Error(
          responseData.message || responseData.error || `HTTP error ${res.status}`
        );
        err.status = res.status;
        err.code = responseData.error || '';
        err.data = responseData;
        throw err;
      }

      return responseData;
    } catch (error) {
      // Avoid logging errors that are intentionally thrown from above (like HTTP status errors)
      if (!error.status) {
        console.error("API request failed:", error);
      }
      // Re-throw the error to be caught by the calling component
      throw error;
    }
  }, [googleIdToken]);

  return { apiRequest };
};
